import { AddressType, BusinessStructureType, RegistrationProgramType } from './register-business-enums.model';
import { IAddress } from '@carm/shared/components/form-elements/models';
import { IDownloadableFileInfo } from '@carm/_utils/models';
import { IntTelFormGroupModel } from '@carm/shared/components/telephone-int/models/telephone-model';

export interface IBn9RegistrationModel extends IValidateBusinessTransactionsModel, IValidateBusinessTransactionsRequest {
  legalEntity?: ILegalEntityModel;
  owners?: IOwnersModel;
  legalName?: ILegalNameModel;
  address?: IAddressModel;
  contacts?: IContactsModel;
  businessActivity?: IBusinessActivityModel;
  scenario?: string;
  businessLegalName?: string;
  hasSystemErrors?: string;
  lastModifiedDate?: string;
  legalEntityValidationStatus?: boolean;
  status?: string;
  submittedDate?: string;
  authorizedOfficial1?: IContact;
  authorizedOfficial2?: IContact;
}

export interface IValidateBusinessTransactionsModel {
  hasPaymentAmount?: boolean;
  hasSOATotals?: boolean;
  hasTransactions?: boolean;
  skipAffinityQuestions?: boolean;
  transactionNumber?: string;
  transactionsValidationStatus?: boolean;
}

export interface IValidateBusinessTransactionsRequest {
  uid?: string;
  businessNumber?: string;
  programNumber?: string;
  programType?: RegistrationProgramType;
  programSubType?: string;
  programId?: string;
}

export interface ILegalEntityModel {
  businessStructure: BusinessStructureType;
  operationType: string;
  jurisdiction?: string;
  jurisdictionProvince?: string;
  jurisdictionCountry?: string;
  jurisdictionState?: string;
  incorporationCertificateId?: string;
  incorporationDate?: Date;
}

export interface ILegalNameModel {
  legalName: string;
  operatingName?: string;
}

export interface IOwnersModel {
  owners: IOwner[];
  authorizedOfficial1: IContact;
  authorizedOfficial2?: IContact;
}

export interface IAddressModel {
  email: string;
  telephone?: string;
  extension?: string;
  fax?: string;
  website?: string;
  physicalAddress: IAddress;
  sameAsPhysical: boolean;
  careOf?: string;
  mailingAddress: IAddress;
  booksKeptInCanada: boolean;
  booksKeptInPlaceOfBusiness?: boolean;
  booksAndRecordsRelationToPrograms?: string;
  sameAsAddress: string;
  recordsAddress: IAddress;
  languagePreference?: string;
  correspondencePreference?: string;
  telIntFormGroup?: IntTelFormGroupModel;
}

export interface IContactsModel {
  genericContactInfo?: IAddressModel;
  contacts: IContact[];
}

export interface IBusinessActivityModel {
  majorActivity: string;
  mainProduct1: string;
  percentRevenue1: string;
  mainProduct2: string;
  percentRevenue2: string;
  mainProduct3: string;
  percentRevenue3: string;
}

export interface IOwner {
  firstName: string;
  lastName: string;
  position: string;
  languagePref: string;
  email: string;
  domesticPhones: IPhone[];
  worldwidePhones: IPhone[];
}

export interface IPhone {
  telephoneType: string;
  deviceType: string;
  areaCode: string;
  countryCode: string;
  cityCode: string;
  telephoneNumber: string;
  telephoneExtensionNumber: string;
}

export interface IContact {
  contactId?: string;
  contactType: string;
  otherContactType: string;
  contactTitle: string;
  otherContactTitle?: string;
  firstName: string;
  lastName: string;
  email: string;
  telephone: string;
  extension?: string;
  fax?: string;
  languagePref: string;
  telIntFormGroup: IntTelFormGroupModel;
}

export interface BusinessProgramIdentifier {
  businessNumber?: string;
  programNumber?: string;
  programId?: string;
  programType?: string;
}

export interface IValidateBusinessModel extends BusinessProgramIdentifier {
  // affinity questions
  uid: string;
  scenario: string;
  legalEntityName: string;
  businessStructure?: string;
  jurisdiction?: string;
  jurisdictionCountry?: string;
  jurisdictionProvince?: string;
  jurisdictionState?: string;
  incorporationCertificateId?: string;
  incorporationDate?: Date;
  address: {
    line1: string;
    line2: string;
    city: string;
    country: string;
    provinceOrState: string;
    postalOrZipCode: string;
  };
  certification: boolean;
  // transactions
  verificationPin: string;
  transactionNumber: string;
  validationQuestion1Id: string;
  validationQuestion1TransactionNumberAnswer: string;
  validationQuestion1TransactionAmountAnswer: number;
  validationQuestion2Id: string;
  validationQuestion2TransactionNumberAnswer: string;
  validationQuestion2TransactionAmountAnswer: number;
  authorizedOfficial1: IContact;
  authorizedOfficial2: IContact;
  contacts: IContactsModel;
  legalName?: ILegalNameModel;
}

export interface ILinkBusinessTransactionsInfo {
  hasTransactions: boolean;
  hasSOATotals: boolean;
  hasPaymentAmount: boolean;
  transactionNumber: string;
}

export interface ILinkBusinessReviewInfo {
  programNumbers: string[];
  businessNumber: string;
  legalName: string;
}

export interface ILinkBusinessProgram {
  programName: string;
  programId: string;
  programNumber: string;
}

export interface IProgramAddress extends IAddress {
  uid?: string;
  sameAs?: string;
  careOf?: string;
  fullAddress?: string;
  addressType?: string;
  addressTypeName?: string;
  telIntFormGroup?: IntTelFormGroupModel;
  telephone?: string;
  fax?: string;
  extension?: string;
  languagePref?: string;
  brAddressRemark?: string;
  booksRecordsAtBusiness?: boolean;
  recordsInCanada?: boolean;
  relateAddressAndBusiness?: string;
  socialMediaAddress?: ISocialMediaAddress[];
}

export interface ISocialMediaAddress {
  addressIdByExternalSystem?: string;
  addressId?: string;
  socialMediaType: string;
  otherSocialMediaType: string;
  socialMediaAddress: string;
}

export interface ISameAsAddressModel {
  address: IProgramAddress;
  addressType: AddressType;
  name: string;
  overrideName?: boolean;
}

export interface ISameAsAddressesModel {
  physical: ISameAsAddressModel[];
  mailing: ISameAsAddressModel[];
  records: ISameAsAddressModel[];
}

export interface IDownloadableTemplateInfo extends IDownloadableFileInfo {
  templateCode: string;
  workFlow?: string;
}

export enum FileTemplateMetadataKey {
  ImporterAgreementBooksOutsideCanada = 'agreement_to_maintain_books_and_records_outside_of_canada',
  CustomsBrokerEnrolmentForm = 'customs_broker_enrolment_form',
  WarehouseCustomsBondedWarehouseEnrolmentForm = 'customs_bonded_warehouse_enrolment_form',
  WarehouseSufferanceWarehouseEnrolmentForm = 'sufferance_warehouse_enrolment_form',
  DutyFreeShopEnrolmentForm = 'duty_free_shop_enrolment_form',
  EdiServiceAdvanceCommercialInformationEnrolmentForm = 'advance_commercial_information_form',
  EdiServiceIntegratedImportDeclarationEnrolmentForm = 'integrated_import_declaration_form',
  EdiServiceDailyNoticeAndStatementOfAccountEnrolmentForm = 'daily_notice_and_SOA_form',
  EdiServiceAcceleratedCommercialReleaseOperationsEnrolmentForm = 'accelerated_commercial_release_operations_support_system_form',
  EdiServiceG7EdiEnrolmentForm = 'g7_edi_form',
  EdiServiceMarineExportReportingEnrolmentForm = 'marine_export_reporting_form',
  EdiServiceReleaseNotificationSystemEnrolmentForm = 'release_notification_system_form',
  EdiServiceCommercialAccountingDeclarationEnrolmentForm = 'commercial_accounting_declaration_form',
  TrustedTrader_IMP_Part1_EnrolmentForm = 'trusted_trader_imp_part1_form',
  TrustedTrader_IMP_Part2_EnrolmentForm = 'trusted_trader_imp_part2_form',
  TrustedTrader_CARRIERS_Part1_EnrolmentForm = 'trusted_trader_carriers_part1_form',
  TrustedTrader_CARRIERS_Part2_EnrolmentForm = 'trusted_trader_carriers_part2_form',
  DutyReliefLicenceEnrolmentForm = 'duty_relief_licence_form',
  ReportingSummaryAccountingVehicleRepairsEnrolmentForm = 'reporting_summary_accounting_vehicle_repairs_form'
  // CoastingTradeLicenceEnrolmentForm = 'coasting_trade_licence_form',
  // DutyRemissionEnrolmentForm = 'duty_remission_form'
}
